import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../AuthContext';
import {
  fetchAdminTextElementById,
  fetchWebliumWebsiteBlockElements,
  fetchWebliumWebsiteBlocks,
  fetchWebliumWebsitePages,
  fetchWebliumWebsites,
  pushAdminBlockContentToWebliumByBlockId,
  runTextElementQuickAction,
  saveAdminTextElementById
} from '../api';
import type {
  TextElementQuickActionType,
  WebliumWebsiteBlockMentionItem,
  WebliumWebsiteElementMentionItem,
  WebliumWebsiteMentionItem,
  WebliumWebsitePageMentionItem
} from '../api.types';
import BlockMenu from '../imports/BlockMenu';
import Background from '../imports/Background';

const TEXT_ELEMENT_TYPES = new Set([
  'blocktitle', 'herotitle', 'subtitle', 'caption', 'heading', 'headinglg', 'subheading',
  'text', 'small', 'primary', 'primary-alt', 'secondary', 'secondary-alt', 'button',
  'categoryheading', 'categorydescription', 'orderheading'
]);

type TextVersion = {
  text: string;
  label: string;
};

type Feedback = { type: 'success' | 'error'; message: string } | null;

const getErrorMessage = (error: unknown, fallback: string): string => (
  error instanceof Error && error.message.trim() ? error.message : fallback
);

const actionLabels: Record<TextElementQuickActionType, string> = {
  make_shorter: 'Make shorter',
  make_longer: 'Make longer',
  rewrite: 'Rewrite'
};

const selectorClassName = 'h-12 w-full rounded-xl border border-[#dce3f0] bg-white px-4 text-[15px] text-[#212736] outline-none transition focus:border-[#165BFB] focus:ring-2 focus:ring-[#165BFB]/10 disabled:cursor-not-allowed disabled:bg-[#f5f7fb] disabled:text-[#9aa2b2]';
const primaryButtonClassName = 'rounded-xl bg-[#165BFB] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#114edc] disabled:cursor-not-allowed disabled:opacity-50';
const secondaryButtonClassName = 'rounded-xl border border-[#cfd9ec] bg-white px-5 py-3 text-sm font-semibold text-[#344054] transition hover:border-[#9fb7e7] hover:bg-[#f8faff] disabled:cursor-not-allowed disabled:opacity-50';

const TextElementQuickActionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const token = localStorage.getItem('authToken') || '';

  const [websites, setWebsites] = useState<WebliumWebsiteMentionItem[]>([]);
  const [pages, setPages] = useState<WebliumWebsitePageMentionItem[]>([]);
  const [blocks, setBlocks] = useState<WebliumWebsiteBlockMentionItem[]>([]);
  const [elements, setElements] = useState<WebliumWebsiteElementMentionItem[]>([]);
  const [websiteId, setWebsiteId] = useState('');
  const [pageId, setPageId] = useState('');
  const [blockId, setBlockId] = useState('');
  const [elementId, setElementId] = useState('');
  const [loadingLevel, setLoadingLevel] = useState<string | null>('websites');
  const [selectionError, setSelectionError] = useState<string | null>(null);

  const [versions, setVersions] = useState<TextVersion[]>([]);
  const [versionIndex, setVersionIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [contentLoading, setContentLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState(false);
  const [activeAction, setActiveAction] = useState<TextElementQuickActionType | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [pushing, setPushing] = useState(false);
  const [pushFeedback, setPushFeedback] = useState<Feedback>(null);

  const selectedWebsite = websites.find((item) => item.id === websiteId);
  const selectedPage = pages.find((item) => item.id === pageId);
  const selectedBlock = blocks.find((item) => item.id === blockId);
  const currentVersion = versions[versionIndex];
  const displayedText = isEditing ? draftText : currentVersion?.text ?? '';
  const selectionComplete = Boolean(websiteId && pageId && blockId && elementId);

  useEffect(() => {
    let cancelled = false;
    setLoadingLevel('websites');
    fetchWebliumWebsites(token)
      .then((items) => {
        if (!cancelled) setWebsites(items);
      })
      .catch((error) => {
        if (!cancelled) setSelectionError(getErrorMessage(error, 'Failed to load websites.'));
      })
      .finally(() => {
        if (!cancelled) setLoadingLevel(null);
      });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!websiteId) return;
    let cancelled = false;
    setLoadingLevel('pages');
    setSelectionError(null);
    fetchWebliumWebsitePages(token, websiteId)
      .then((items) => {
        if (!cancelled) setPages(items);
      })
      .catch((error) => {
        if (!cancelled) setSelectionError(getErrorMessage(error, 'Failed to load pages.'));
      })
      .finally(() => {
        if (!cancelled) setLoadingLevel(null);
      });
    return () => { cancelled = true; };
  }, [token, websiteId]);

  useEffect(() => {
    if (!websiteId || !pageId) return;
    let cancelled = false;
    setLoadingLevel('blocks');
    setSelectionError(null);
    fetchWebliumWebsiteBlocks(token, websiteId, pageId)
      .then((items) => {
        if (!cancelled) setBlocks(items);
      })
      .catch((error) => {
        if (!cancelled) setSelectionError(getErrorMessage(error, 'Failed to load blocks.'));
      })
      .finally(() => {
        if (!cancelled) setLoadingLevel(null);
      });
    return () => { cancelled = true; };
  }, [token, websiteId, pageId]);

  useEffect(() => {
    if (!websiteId || !pageId || !blockId) return;
    let cancelled = false;
    setLoadingLevel('elements');
    setSelectionError(null);
    fetchWebliumWebsiteBlockElements(token, websiteId, pageId, blockId)
      .then((items) => {
        if (!cancelled) {
          setElements(items.filter((item) => item.type && TEXT_ELEMENT_TYPES.has(item.type)));
        }
      })
      .catch((error) => {
        if (!cancelled) setSelectionError(getErrorMessage(error, 'Failed to load text elements.'));
      })
      .finally(() => {
        if (!cancelled) setLoadingLevel(null);
      });
    return () => { cancelled = true; };
  }, [token, websiteId, pageId, blockId]);

  useEffect(() => {
    if (!selectionComplete) return;
    let cancelled = false;
    setContentLoading(true);
    setFeedback(null);
    fetchAdminTextElementById(token, websiteId, pageId, blockId, elementId)
      .then((element) => {
        if (cancelled) return;
        setVersions([{ text: element.content, label: 'Original' }]);
        setVersionIndex(0);
        setDraftText(element.content);
        setIsEditing(false);
      })
      .catch((error) => {
        if (!cancelled) setFeedback({ type: 'error', message: getErrorMessage(error, 'Failed to load text content.') });
      })
      .finally(() => {
        if (!cancelled) setContentLoading(false);
      });
    return () => { cancelled = true; };
  }, [token, selectionComplete, websiteId, pageId, blockId, elementId]);

  const resetContent = () => {
    setVersions([]);
    setVersionIndex(0);
    setDraftText('');
    setIsEditing(false);
    setFeedback(null);
    setPushFeedback(null);
  };

  const handleWebsiteChange = (nextWebsiteId: string) => {
    setWebsiteId(nextWebsiteId);
    setPageId('');
    setBlockId('');
    setElementId('');
    setPages([]);
    setBlocks([]);
    setElements([]);
    resetContent();
  };

  const handlePageChange = (nextPageId: string) => {
    setPageId(nextPageId);
    setBlockId('');
    setElementId('');
    setBlocks([]);
    setElements([]);
    resetContent();
  };

  const handleBlockChange = (nextBlockId: string) => {
    setBlockId(nextBlockId);
    setElementId('');
    setElements([]);
    resetContent();
  };

  const handleElementChange = (nextElementId: string) => {
    setElementId(nextElementId);
    resetContent();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleEdit = () => {
    setDraftText(currentVersion?.text ?? '');
    setIsEditing(true);
    setFeedback(null);
  };

  const handleCancel = () => {
    setDraftText(currentVersion?.text ?? '');
    setIsEditing(false);
    setFeedback(null);
  };

  const handleSave = async () => {
    if (!selectionComplete) return;
    setSaving(true);
    setFeedback(null);
    try {
      const saved = await saveAdminTextElementById(token, websiteId, pageId, blockId, elementId, draftText);
      if (saved.content !== currentVersion?.text) {
        setVersions((items) => {
          const nextItems = [...items, { text: saved.content, label: 'Manual edit' }];
          setVersionIndex(nextItems.length - 1);
          return nextItems;
        });
      }
      setDraftText(saved.content);
      setIsEditing(false);
      setFeedback({ type: 'success', message: saved.modified ? 'Text saved to the block.' : 'Text is already up to date.' });
    } catch (error) {
      setFeedback({ type: 'error', message: getErrorMessage(error, 'Failed to save text.') });
    } finally {
      setSaving(false);
    }
  };

  const handleQuickAction = async (action: TextElementQuickActionType) => {
    if (!selectionComplete || !currentVersion?.text.trim()) return;
    setActiveAction(action);
    setFeedback(null);
    try {
      const result = await runTextElementQuickAction(
        token,
        websiteId,
        pageId,
        blockId,
        elementId,
        action,
        currentVersion.text
      );
      setVersions((items) => {
        const nextItems = [...items, { text: result.text, label: actionLabels[action] }];
        setVersionIndex(nextItems.length - 1);
        return nextItems;
      });
      setDraftText(result.text);
      setFeedback({ type: 'success', message: `${actionLabels[action]} created a new in-memory version.` });
    } catch (error) {
      setFeedback({ type: 'error', message: getErrorMessage(error, 'Quick action failed.') });
    } finally {
      setActiveAction(null);
    }
  };

  const handleUseVersion = async () => {
    if (!selectionComplete || !currentVersion || versionIndex === versions.length - 1) return;
    setRestoringVersion(true);
    setFeedback(null);
    try {
      const saved = await saveAdminTextElementById(
        token,
        websiteId,
        pageId,
        blockId,
        elementId,
        currentVersion.text
      );
      setVersions((items) => {
        const nextItems = [...items, { text: saved.content, label: 'Restored version' }];
        setVersionIndex(nextItems.length - 1);
        return nextItems;
      });
      setDraftText(saved.content);
      setFeedback({ type: 'success', message: 'Selected version saved to the block as the latest version.' });
    } catch (error) {
      setFeedback({ type: 'error', message: getErrorMessage(error, 'Failed to use this version.') });
    } finally {
      setRestoringVersion(false);
    }
  };

  const handlePush = async () => {
    if (!websiteId || !pageId || !blockId) return;
    setPushing(true);
    setPushFeedback(null);
    try {
      const result = await pushAdminBlockContentToWebliumByBlockId(token, websiteId, pageId, blockId);
      setPushFeedback({ type: 'success', message: result.message });
    } catch (error) {
      setPushFeedback({ type: 'error', message: getErrorMessage(error, 'Failed to push block to Weblium.') });
    } finally {
      setPushing(false);
    }
  };

  const selectorSteps = useMemo(() => [
    { label: 'Website', complete: Boolean(websiteId) },
    { label: 'Page', complete: Boolean(pageId) },
    { label: 'Block', complete: Boolean(blockId) },
    { label: 'Text element', complete: Boolean(elementId) }
  ], [websiteId, pageId, blockId, elementId]);

  return (
    <div className="relative min-h-screen bg-[#f7f9fe] text-[#212736]">
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden opacity-50">
        <div className="scale-90 origin-top"><Background /></div>
      </div>
      <div className="relative z-10 min-h-screen">
        <header className="bg-white"><BlockMenu onLogout={handleLogout} showSessionControls={false} /></header>

        <main className="mx-auto w-full max-w-[1180px] px-4 py-8 md:px-8 md:py-12">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <div className="mb-8 max-w-[760px]">
              <div className="mb-3 inline-flex rounded-full bg-[#eaf1ff] px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-[#165BFB]">Content tools</div>
              <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Text element quick actions</h1>
              <p className="mt-3 text-base leading-7 text-[#676d7d]">Choose a text element, refine its copy, and save only when the result is ready. Generated versions stay in this browser tab.</p>
            </div>

            <section className="mb-6 rounded-3xl border border-[#e1e7f2] bg-white p-5 shadow-[0_16px_45px_rgba(36,55,92,0.08)] md:p-8">
              <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-center">
                <div>
                  <h2 className="text-xl font-bold">1. Select an element</h2>
                  <p className="mt-1 text-sm text-[#7b8291]">Selections narrow from website to the exact editable text.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectorSteps.map((step, index) => (
                    <span key={step.label} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${step.complete ? 'bg-[#e9f7ef] text-[#18864b]' : 'bg-[#f2f4f8] text-[#8a91a0]'}`}>
                      {step.complete ? '✓' : index + 1} {step.label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <label className="text-sm font-semibold text-[#464c5c]">Website
                  <select className={`${selectorClassName} mt-2`} value={websiteId} onChange={(event) => handleWebsiteChange(event.target.value)} disabled={loadingLevel === 'websites'}>
                    <option value="">{loadingLevel === 'websites' ? 'Loading websites…' : 'Select website'}</option>
                    {websites.map((website) => <option key={website.id} value={website.id}>{website.name || website.domainLabel}</option>)}
                  </select>
                </label>
                <label className="text-sm font-semibold text-[#464c5c]">Page
                  <select className={`${selectorClassName} mt-2`} value={pageId} onChange={(event) => handlePageChange(event.target.value)} disabled={!websiteId || loadingLevel === 'pages'}>
                    <option value="">{loadingLevel === 'pages' ? 'Loading pages…' : 'Select page'}</option>
                    {pages.map((page) => <option key={page.id} value={page.id}>{page.label || page.name}</option>)}
                  </select>
                </label>
                <label className="text-sm font-semibold text-[#464c5c]">Block
                  <select className={`${selectorClassName} mt-2`} value={blockId} onChange={(event) => handleBlockChange(event.target.value)} disabled={!pageId || loadingLevel === 'blocks'}>
                    <option value="">{loadingLevel === 'blocks' ? 'Loading blocks…' : 'Select block'}</option>
                    {blocks.map((block) => <option key={block.id} value={block.id}>{block.label || block.name || 'Untitled block'}</option>)}
                  </select>
                </label>
                <label className="text-sm font-semibold text-[#464c5c]">Text element
                  <select className={`${selectorClassName} mt-2`} value={elementId} onChange={(event) => handleElementChange(event.target.value)} disabled={!blockId || loadingLevel === 'elements'}>
                    <option value="">{loadingLevel === 'elements' ? 'Loading text elements…' : 'Select text element'}</option>
                    {elements.map((element) => <option key={element.id} value={element.id}>{element.label || element.name}</option>)}
                  </select>
                </label>
              </div>
              {selectionError && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{selectionError}</p>}
              {blockId && loadingLevel !== 'elements' && elements.length === 0 && <p className="mt-4 text-sm text-[#7b8291]">This block has no supported text elements.</p>}
            </section>

            <section className={`mb-6 rounded-3xl border bg-white p-5 shadow-[0_16px_45px_rgba(36,55,92,0.08)] transition md:p-8 ${selectionComplete ? 'border-[#d8e3fb]' : 'border-[#e7eaf0] opacity-70'}`}>
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-xl font-bold">2. Review and refine</h2>
                  <p className="mt-1 text-sm text-[#7b8291]">AI actions create previews. Saving to the database is always explicit.</p>
                </div>
              </div>

              {!selectionComplete ? (
                <div className="flex min-h-[210px] items-center justify-center rounded-2xl border border-dashed border-[#d8deea] bg-[#fafbfe] px-6 text-center text-[#8a91a0]">Select a website, page, block, and text element to see its content.</div>
              ) : contentLoading ? (
                <div className="flex min-h-[210px] items-center justify-center rounded-2xl bg-[#fafbfe] text-[#65708a]">Loading text content…</div>
              ) : (
                <>
                  <textarea
                    className={`min-h-[220px] w-full resize-y rounded-2xl border p-5 text-[16px] leading-7 outline-none transition ${isEditing ? 'border-[#165BFB] bg-white ring-4 ring-[#165BFB]/10' : 'border-[#dce3ef] bg-[#f9fbff] text-[#3b4252]'}`}
                    value={displayedText}
                    onChange={(event) => setDraftText(event.target.value)}
                    readOnly={!isEditing}
                    aria-label="Text element content"
                  />

                  {versions.length > 0 && (
                    <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
                      <button className="flex size-10 items-center justify-center rounded-full border border-[#d7dfed] bg-white text-lg hover:bg-[#f5f8ff] disabled:opacity-35" onClick={() => setVersionIndex((index) => Math.max(0, index - 1))} disabled={versionIndex === 0 || isEditing} aria-label="Previous text version">←</button>
                      <span className="min-w-[190px] rounded-full bg-[#f2f5fb] px-4 py-2 text-center text-xs font-semibold text-[#596174]">
                        {currentVersion?.label} · Version {versionIndex + 1} of {versions.length}
                      </span>
                      <button className="flex size-10 items-center justify-center rounded-full border border-[#d7dfed] bg-white text-lg hover:bg-[#f5f8ff] disabled:opacity-35" onClick={() => setVersionIndex((index) => Math.min(versions.length - 1, index + 1))} disabled={versionIndex === versions.length - 1 || isEditing} aria-label="Next text version">→</button>
                      {versionIndex < versions.length - 1 && (
                        <button className="rounded-full border border-[#cfd9ec] bg-white px-4 py-2 text-xs font-semibold text-[#344054] transition hover:border-[#9fb7e7] hover:bg-[#f8faff] disabled:cursor-not-allowed disabled:opacity-50" onClick={handleUseVersion} disabled={isEditing || restoringVersion || saving || Boolean(activeAction)}>
                          {restoringVersion ? 'Saving…' : 'Use this version'}
                        </button>
                      )}
                    </div>
                  )}

                  <div className="mt-5 flex flex-wrap gap-3">
                    {!isEditing ? (
                      <button className={secondaryButtonClassName} onClick={handleEdit} disabled={!currentVersion || Boolean(activeAction)}>Edit manually</button>
                    ) : (
                      <>
                        <button className={primaryButtonClassName} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                        <button className={secondaryButtonClassName} onClick={handleCancel} disabled={saving}>Cancel</button>
                      </>
                    )}
                    <div className="hidden h-10 w-px self-center bg-[#e3e7ef] sm:block" />
                    {(Object.keys(actionLabels) as TextElementQuickActionType[]).map((action) => (
                      <button key={action} className={secondaryButtonClassName} onClick={() => handleQuickAction(action)} disabled={isEditing || Boolean(activeAction) || !currentVersion?.text.trim()}>
                        {activeAction === action ? 'Working…' : actionLabels[action]}
                      </button>
                    ))}
                  </div>
                  {feedback && <p className={`mt-4 rounded-xl px-4 py-3 text-sm ${feedback.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{feedback.message}</p>}
                </>
              )}
            </section>

            <section className="rounded-3xl border border-[#e1e7f2] bg-white p-5 shadow-[0_16px_45px_rgba(36,55,92,0.08)] md:p-8">
              <div className="mb-5">
                <h2 className="text-xl font-bold">3. Push block to Weblium <span className="ml-2 align-middle text-xs font-semibold text-[#8a91a0]">Optional</span></h2>
                <p className="mt-1 text-sm text-[#7b8291]">Publish the selected block’s stored content through the existing push flow.</p>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <label className="text-sm font-semibold text-[#7b8291]">Website<select className={`${selectorClassName} mt-2`} disabled value={websiteId}><option value={websiteId}>{selectedWebsite?.name || 'Not selected'}</option></select></label>
                <label className="text-sm font-semibold text-[#7b8291]">Page<select className={`${selectorClassName} mt-2`} disabled value={pageId}><option value={pageId}>{selectedPage?.label || selectedPage?.name || 'Not selected'}</option></select></label>
                <label className="text-sm font-semibold text-[#7b8291]">Block<select className={`${selectorClassName} mt-2`} disabled value={blockId}><option value={blockId}>{selectedBlock?.label || selectedBlock?.name || 'Not selected'}</option></select></label>
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-4">
                <button className={primaryButtonClassName} onClick={handlePush} disabled={!blockId || pushing}>{pushing ? 'Pushing…' : 'Push block'}</button>
                <p className="text-xs text-[#8a91a0]">Only the same block selected above can be pushed.</p>
              </div>
              {pushFeedback && <p className={`mt-4 rounded-xl px-4 py-3 text-sm ${pushFeedback.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{pushFeedback.message}</p>}
            </section>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default TextElementQuickActionsPage;
